import { useEffect } from 'react';

/**
 * Efeitos visuais da página inicial: revelação ao rolar (IntersectionObserver)
 * e barra de progresso. Só adiciona classes CSS; não altera a estrutura do DOM.
 */
export default function usePageEffects(ready = true) {
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const bar = document.querySelector('.pp-progress');

    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      bar?.style.setProperty('--pp-scroll', String(ratio));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Brilho suave que acompanha o mouse nos cartões (só em dispositivos com mouse).
    let raf = 0;
    const canHover = window.matchMedia?.('(hover: hover)').matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const card = e.target.closest?.('main .rounded-3xl.bg-white, main .rounded-2xl.bg-white');
        if (!card) return;
        const r = card.getBoundingClientRect();
        card.classList.add('pp-spot');
        card.style.setProperty('--mx', `${e.clientX - r.left}px`);
        card.style.setProperty('--my', `${e.clientY - r.top}px`);
      });
    };
    if (canHover) document.addEventListener('pointermove', onMove, { passive: true });

    if (reduce || !('IntersectionObserver' in window)) {
      return () => { window.removeEventListener('scroll', onScroll); document.removeEventListener('pointermove', onMove); };
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('pp-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );

    // Blocos principais de cada seção; os filhos diretos de grids entram em cascata.
    const targets = [];
    document.querySelectorAll('main > section').forEach((section) => {
      const container = section.firstElementChild?.matches('div') ? section.firstElementChild : section;
      Array.from(container.children).forEach((child, i) => {
        if (child.matches('.grid') || child.matches('[class*="grid-cols"]')) {
          Array.from(child.children).forEach((item, j) => {
            item.style.setProperty('--pp-delay', `${Math.min(j, 6) * 90}ms`);
            targets.push(item);
          });
        } else {
          child.style.setProperty('--pp-delay', `${Math.min(i, 3) * 70}ms`);
          targets.push(child);
        }
      });
    });
    targets.forEach((el) => {
      // Elementos já visíveis no primeiro paint (hero) não devem "piscar".
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.9 && r.bottom > 0) return;
      el.classList.add('pp-reveal');
      io.observe(el);
    });

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [ready]); // roda de novo quando as seções carregadas depois do topo chegam
}
